/* eslint-disable react-hooks/exhaustive-deps */
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Image,
  Skeleton,
  Text,
  Textarea,
  useBoolean,
  VStack,
} from '@chakra-ui/react';
import { useToastr } from '@laxmimanogna/code-quick-components';
import React, { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { EmailIcon } from '@chakra-ui/icons';
import HelpSupportImage from '../../assets/images/HelpPageImage.svg';
import { withProvider } from '../../hoc/withProvider';
import MyAccountProvider, {
  MyAccountContext,
} from '../../providers/MyAccountProvider';
import faqHelpRepository from '../../repositories/FaqRepository';

const feedbackSchema = yup.object().shape({
  feedback_text: yup
    .string()
    .required('Feedback is required')
    .max(500, 'Feedback must be at most 500 characters'),
});

const HelpContainer = () => {
  const toast = useToastr();
  const [isFetching, ifState] = useBoolean(false);
  const [faqQuestions, setFaqQuestions] = useState([]);
  const myAccountContext = useContext(MyAccountContext);
  const currentUser = myAccountContext.currentUserType;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: yupResolver(feedbackSchema),
  });

  const onFeedbackSubmit = async values => {
    try {
      await myAccountContext.postClientFeedback({
        client_id: currentUser.id,
        ...values,
      });
      reset();
    } catch (error) {
      // Provider is expected to handle error toasts
    }
  };

  const getFaqQuestions = async () => {
    try {
      ifState.on();
      const response = await faqHelpRepository.getFaqQuestions();
      // setFaqQuestions([]);
      setFaqQuestions(response);
    } catch (error) {
      toast.showError({
        description: error.toString(),
      });
    } finally {
      ifState.off();
    }
  };

  React.useEffect(() => {
    myAccountContext.userTypeLookup();
    getFaqQuestions();
  }, []);

  const renderFaqAccordion = () => (
    <Accordion allowToggle>
      {faqQuestions
        .sort((q, p) => q.order - p.order)
        .filter(f => f.is_active)
        .map((faq, i) => (
          <AccordionItem
            key={i}
            mb={4}
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="lg"
            bg="white"
            boxShadow="sm"
          >
            <h2>
              <AccordionButton
                _hover={{ bg: 'gray.50' }}
                p={5}
                borderRadius="lg"
              >
                <Box
                  flex="1"
                  textAlign="left"
                  fontWeight="semibold"
                  fontSize="md"
                >
                  {faq.question}
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={5} px={5}>
              <Text color="gray.600">{faq.answer}</Text>
            </AccordionPanel>
          </AccordionItem>
        ))}
    </Accordion>
  );

  const renderFaqLoader = () => (
    <VStack spacing={4} align="stretch">
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} height="75px" borderRadius="lg" />
      ))}
    </VStack>
  );

  const renderFeedbackForm = () => (
    <Box
      p={{ base: 6, md: 8 }}
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="lg"
      boxShadow="sm"
    >
      <Heading as="h3" size="lg" mb={6}>
        Submit Feedback
      </Heading>
      <form onSubmit={handleSubmit(onFeedbackSubmit)}>
        <VStack spacing={4} align="stretch">
          <FormControl isInvalid={errors.feedback_text}>
            <FormLabel htmlFor="feedback_text" fontWeight="semibold">
              Your Feedback
            </FormLabel>
            <Textarea
              id="feedback_text"
              placeholder="Share your thoughts..."
              {...register('feedback_text')}
              rows={5}
              bg="gray.50"
              focusBorderColor="blue.500"
              maxLength={500}
            />
            <FormErrorMessage>
              {errors.feedback_text && errors.feedback_text.message}
            </FormErrorMessage>
          </FormControl>
          <Button colorScheme="blue" isLoading={isSubmitting} type="submit">
            Submit Feedback
          </Button>
        </VStack>
      </form>
    </Box>
  );

  const renderSupportAndFeedback = () => (
    <VStack spacing={8} align="stretch">
      {/* Contact Card */}
      <Box
        p={6}
        bg="white"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="lg"
        boxShadow="sm"
      >
        <Heading as="h3" size="lg" mb={4}>
          Contact Support
        </Heading>
        <Text color="gray.600" mb={4}>
          Can't find an answer? Our team is here to assist you.
        </Text>
        <HStack
          as="a"
          href="mailto:support@code-quick.com"
          spacing={3}
          p={3}
          borderRadius="md"
          transition="background 0.2s"
          _hover={{ bg: 'blue.50', textDecoration: 'none' }}
          display="inline-flex"
        >
          <EmailIcon color="blue.500" />
          <Text fontWeight="medium" color="blue.600">
            support@code-quick.com
          </Text>
        </HStack>
      </Box>

      {/* Feedback Form */}
      {currentUser && renderFeedbackForm()}
    </VStack>
  );

  const hasFaqs = isFetching || faqQuestions.length > 0;

  return (
    <Box bg="gray.50" minH="100vh" py={{ base: 6, md: 10 }}>
      <Box maxW="1200px" mx="auto" px={{ base: 4, md: 8 }}>
        {/* Header */}
        <HStack
          spacing={{ base: 4, md: 8 }}
          mb={{ base: 8, md: 12 }}
          bg="white"
          p={{ base: 6, md: 8 }}
          borderRadius="xl"
          boxShadow="sm"
          borderWidth="1px"
          borderColor="gray.200"
        >
          <Image
            display={{ base: 'none', md: 'block' }}
            src={HelpSupportImage}
            alt="Help and support"
            boxSize={'120px'}
          />
          <VStack spacing={2} align="flex-start">
            <Heading
              as="h1"
              size={{ base: 'xl', md: '2xl' }}
              fontWeight="bold"
            >
              Help & Support
            </Heading>
            <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600">
              We're here to help you get the most out of RevIntegrity. Find
              answers, contact support, and leave feedback.
            </Text>
          </VStack>
        </HStack>

        {hasFaqs ? (
          <Grid
            templateColumns={{ base: '1fr', lg: '2fr 1fr' }}
            gap={{ base: 8, md: 10 }}
          >
            {/* Left Column: FAQs */}
            <GridItem as="main">
              <Heading as="h2" size="xl" mb={6}>
                Frequently Asked Questions
              </Heading>
              {isFetching ? renderFaqLoader() : renderFaqAccordion()}
            </GridItem>

            {/* Right Column: Support & Feedback */}
            <GridItem as="aside">{renderSupportAndFeedback()}</GridItem>
          </Grid>
        ) : (
          <Box maxW="680px" mx="auto">
            {renderSupportAndFeedback()}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default withProvider(MyAccountProvider, HelpContainer);
